import {
  Button,
  Card,
  ClickAwayListener,
  MenuItem,
  MenuList,
  Popper,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { environment } from 'environment';
import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ChevronDownIcon } from '../icons/chevron-down';
import { ChevronRightOutlinedIcon } from '../icons/chevron-right-outlined';
import { CloseOutlinedIcon } from '../icons/close-outlined';
import { CascadingMenuItem } from './cascading-menu-item';
import './cascading-menu.scss';
import { MenuProps } from './menu.props';
import { SubMenuItem } from './sub-menu/sub-menu-item';

/**
 * A simple dropdown/popover represented by a button.
 *
 * Items are passed in as props.
 * @param props
 */
export const CascadingMenu: FunctionComponent<MenuProps> = (props) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const mediaQuery = `(min-width: ${theme.breakpoints.values.sm}px)`;
  const desktop = useMediaQuery(mediaQuery);

  /* For *TEMA* */
  const { temaFeatures } = environment.featureFlags;
  /* END For *TEMA* */

  // sets component width when open
  const [width, setWidth] = useState<undefined | number>(undefined);

  // controls opening dropdown
  const [open, setOpen] = useState(false);

  // sets button placeholder
  const [buttonTitle, setButtonTitle] = useState<string | React.ReactNode | null | undefined>(props.default);

  // stores selected option
  const [selectedOption, setSelectedOption] = useState<CascadingMenuItem | null>(props.default);

  // controls opening of submenu for particular option.
  const [anchorSec, setAnchorSec] = React.useState<null | HTMLElement>(null);

  /**
   * Opens subMenu (second level).
   * @param event event trigger
   */
  const openSecMenu = (event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>) => {
    setAnchorSec(event.currentTarget);

    // sets submenu label on top of dropdown
    if (!desktop) {
      setButtonTitle(event.currentTarget.id);
    }
    event.stopPropagation();
  };

  /**
   * Closes subMenu (second level).
   * @param event event trigger
   */
  const closeSecMenu = (event: React.MouseEvent<EventTarget>) => {
    setAnchorSec(null);
    // sets proper button title on first component render and when switching between options on mobile view
    if (props.default === selectedOption) {
      setButtonTitle(props.default);
    } else if (selectedOption) {
      setButtonTitle(selectedOption.value);
    } else {
      setButtonTitle('');
    }

    // prevents bubbling click event on back button up to dropdown button (and closing all menu)
    event.stopPropagation();
  };

  /**
   * Toggles the dropdown open state.
   */
  const toggle = (event: React.MouseEvent<EventTarget>) => {
    setOpen((isOpen) => !isOpen);
    if (open) { closeSecMenu(event); }
  };

  /**
   * Closes the dropdown.
   */
  const close = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    closeSecMenu(event);
    setOpen(false);
  };

  /**
   * Dynamically resolves classes that should be added to element.
   * @param type element to add class
   * @param item element's data to use in ternary expression
   */
  const addClass = (item: any, type?: 'menuOption' | 'subMenu' | 'subMenuOption') => {
    let elType;
    let selectedOptionValue;

    if (selectedOption?.value) {
      selectedOptionValue = selectedOption?.id;
    } else {
      // assigns class to default selected option
      selectedOptionValue = props.default;
    }

    // sets type of first level option
    if (!type) {
      elType = item.subOptions ? 'subMenu' : 'menuOption';
    } else {
      elType = type;
    }
    let classes;
    let hiddenClass;
    const activeClass = item.id === anchorSec?.id ? 'active' : '';
    const selectedClass = item.id === selectedOptionValue ? 'selected' : '';

    switch (elType) {
      case 'menuOption':
        hiddenClass = anchorSec ? 'option-hidden' : '';
        classes = `${selectedClass} ${hiddenClass}`;
        break;
      case 'subMenu':
        hiddenClass = anchorSec && !activeClass ? 'option-hidden' : '';
        classes = `subMenu ${activeClass} ${hiddenClass}`;
        break;
      case 'subMenuOption':
        classes = `subMenu__element ${selectedClass}`;
        break;
      default:
        classes = '';
    }

    return classes;
  };

  /**
   * Closes the dropdown popover and calls the selected item's `action` function.
   * @param event
   * @param item The selected item.
   */
  const itemSelected = (event: React.MouseEvent<EventTarget>, item: CascadingMenuItem) => {
    // emits selected option only if selection changed
    if (buttonTitle !== item.value) {
      if (props.selected) {
        // emits selected option to parent
        props.selected(item);
      }
    }

    close(event);
    setSelectedOption(item);
    setButtonTitle(item.value);
  };

  /**
   * Calls action depends on element and event.
   * @param event trigger
   * @param item triggered element
   */
  const menuItemAction = (event: React.MouseEvent<HTMLElement>, item: CascadingMenuItem) => {
    let action;

    if (item.subOptions) {
      action = openSecMenu(event);
    } else if (event.type === 'click') {
      action = itemSelected(event, item);
    } else {
      action = closeSecMenu(event);
    }
    return action;
  };

  const getDropdownRef = (el: any) => {
    setWidth(el?.popper.offsetWidth);
  };

  useEffect(() => {
    // resets button title when new props are passed
    if (!props.default) {
      setButtonTitle('');
      setSelectedOption(null);
    }
  }, [props.default, props.items]);

  /**
   * Prevents dropdown from overflowing page container by stretching height to fit content
   */
  useEffect(() => {
    const appComponent = document.getElementById('app');
    if (open) {
      const documentHeight = document.documentElement.scrollHeight;
      appComponent?.setAttribute('style', `height: ${documentHeight}px`); // eslint-disable-line
    } else {
      appComponent?.setAttribute('style', 'height: auto'); // eslint-disable-line
    }
  }, [open]);

  return (
    <div
      className={`cascadingMenu ${open ? 'opened' : 'closed'}`}
      style={{ minWidth: width }}
      id={props.id}
    >
      <Button
        onClick={(e) => toggle(e)}
        ref={anchorRef}
        variant="text"
        className="cascadingMenu__button"
      >
        {
          (!desktop && anchorSec) && <ChevronRightOutlinedIcon onClick={closeSecMenu} />
        }
        <span>{buttonTitle}</span>
        {
          (!desktop && open) ? <CloseOutlinedIcon /> : <ChevronDownIcon />
        }
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition={false}
        className="cascadingMenu__container"
        disablePortal
        container={anchorRef.current}
        popperRef={(e) => getDropdownRef(e)}
        // prevents popper from changing placement on mobile
        modifiers={
          {
            flip: {
              enabled: false,
            },
            preventOverflow: {
              enabled: true,
              boundariesElement: 'window',
            },
          }
        }
      >
        <ClickAwayListener onClickAway={(e) => close(e)}>
          <MenuList>
            {props.items.map((item, i) => (
              <MenuItem
                id={item.id}
                key={item.id}
                onClick={(e) => menuItemAction(e, item)}
                onMouseEnter={(e) => menuItemAction(e, item)}
                className={addClass(item)}
              >
                {item.subOptions
                  // first level option with submenu
                  ? (
                    <>
                      <span className="subMenu__label">
                        {item.label}
                        <ChevronRightOutlinedIcon />
                      </span>
                      <>
                        {!desktop
                          // submenu for !desktop
                          ? (
                            <SubMenuItem
                              items={item.subOptions}
                              optionSelected={(e, el) => itemSelected(e, el)}
                              styleClass={(el) => addClass(el, 'subMenuOption')}
                            />
                          )
                          // submenu for desktop (cascading)
                          : (
                            <Popper
                              open={Boolean(anchorSec) && (item.id === anchorSec?.id)}
                              anchorEl={anchorSec}
                              placement="right-start"
                              className="subMenu__option"
                              disablePortal
                            >
                              <Card>
                                <SubMenuItem
                                  items={item.subOptions}
                                  optionSelected={(e, el) => itemSelected(e, el)}
                                  styleClass={(el) => addClass(el, 'subMenuOption')}
                                />
                              </Card>
                            </Popper>
                          )}
                      </>
                    </>
                  )
                // first level option without submenu
                  : (
                    <span className="subMenu__label">
                      {item.label}
                    </span>
                  )}
              </MenuItem>
            ))}
          </MenuList>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};
